import { useMemo } from 'react';
import { BaseFields } from '../fields/types';
import { BaseValidationError } from '../types';

import { $internals, FormActionsMap, FormDeclaration, FormInstance } from './types';
import { getScrollFormErrorProps, type ScrollFormErrorProps } from './scrollToFormError';

/** Параметры хука `useForm` */
type UseFormFieldsOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
> = {
    /** Форма, состояние и поля которой нужно получить */
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    /** Идентификатор формы */
    id: string;
};

/** Возвращает инстанс формы со стабильными ссылками */
export function useFormInstance<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    id,
}: UseFormFieldsOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue
>): Omit<
    FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    'isInitialized' | typeof $internals
> & {
    getScrollProps: () => ScrollFormErrorProps;
} {
    const formInstance = useMemo(() => form.getInstance(id), [form, id]);
    const formInitializationId = formInstance[$internals].useFormInitializationId();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Необходимо подписаться на обновление formInitializationId для корректной работы reinitialize
    const actualFormInstance = useMemo(() => form.getInstance(id), [form, id, formInitializationId]);

    // TODO DIRECT-282944 Обсудить как тут поменять логику, чтобы с последним instance работать всегда
    return useMemo(() => {
        const submit = actualFormInstance.submit.bind(actualFormInstance);
        const addSubmitError = actualFormInstance.addSubmitError.bind(actualFormInstance);
        const addValidationError = actualFormInstance.addValidationError.bind(actualFormInstance);
        const addValidationErrors = actualFormInstance.addValidationErrors.bind(actualFormInstance);
        const revalidate = actualFormInstance.revalidate.bind(actualFormInstance);
        const reset = actualFormInstance.reset.bind(actualFormInstance);
        const reinitialize = actualFormInstance.reinitialize.bind(actualFormInstance);
        const getState = actualFormInstance.getState.bind(actualFormInstance);
        const getContext = actualFormInstance.getContext.bind(actualFormInstance);
        const setContext = actualFormInstance.setContext.bind(actualFormInstance);
        const getValue = actualFormInstance.getValue.bind(actualFormInstance);
        const scrollToFirstError = actualFormInstance.scrollToFirstError.bind(actualFormInstance);
        const subscribe = actualFormInstance.subscribe.bind(actualFormInstance);

        return {
            id,
            name: form.name,
            fields: actualFormInstance.fields,
            actions: actualFormInstance.actions,
            getScrollProps: () => getScrollFormErrorProps({ formName: form.name, formId: id }),
            submit,
            addSubmitError,
            addValidationError,
            addValidationErrors,
            revalidate,
            reset,
            reinitialize,
            scrollToFirstError,
            getValue,
            getState,
            getContext,
            setContext,
            subscribe,
        };
    }, [actualFormInstance, form.name, id]);
}
